import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { formatBigNumber, i18nIntl } from '../../lib/ep.js';

import ActionLink from '../builder/ActionLink';
import EPPlatformVersion from '../builder/EPPlatformVersion';
import FloatingLayer from '../builder/FloatingLayer';
import SectionTitle from '../builder/SectionTitle';
import Tooltip from '../builder/Tooltip';

const BooleanIcon = ({ value, ...props }) => {
	let classes = ['is_boolean'];
	classes.push((value) ? 'knk-on greenIcon' : 'knk-off redIcon');

	return (
		<span class={classes.join(" ")} />
	)
};

const BasicPlansComparisonTable = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display, setDisplay ] = useState(false);

	const min_basic = { basic:true, basicpro:true };
	const min_basic_pro = { basic:false, basicpro:true };


	const sections = [{
		label:false,
		versions:min_basic_pro,
		data:[{
			label:`${i18n("Price per month")} (${window.currency})`,
			versions:{ basic:`29 ${window.currency} / ${i18n("month")}`, basicpro:`49 ${window.currency} / ${i18n("month")}` }
		}, {
			label:i18n("Minimum length of subscription"),
			tooltip:"All plans have a minimum subscription of 1 month. If you don't cancel your plan, it will automatically be renewed after 30 days. Subscribe for a full year and save 2 months!",
			versions:{ basic:i18n("1 month"), basicpro:i18n("1 month") },
		}, {
			label:i18n("Number of sweepstakes included"),
			tooltip:"All active plans include unlimited sweepstakes.",
			versions:{ basic:i18n("***Unlimited"), basicpro:i18n("***Unlimited") },
		}, {
			label:i18n("Number of social media profiles supported"),
			tooltip:"You can run unlimited promotions on any social media profile of which you are an administrator. This applies to your own profiles, and profiles which you administer on behalf of your clients.",
			versions:{ basic:i18n("***Unlimited"), basicpro:i18n("***Unlimited") },
		}, {
			label:i18n("Maximum number of participants per promotion"),
			tooltip:"The Basic plan is limited to a maximum of 5000 entries per promotion. For example, an Instagram giveaway would be limited to 5000 comments. A giveaway with registration form would be limited to 5000 registered leads.",
			versions:{ basic:'5000', basicpro:i18n("Unlimited") },
		}, {
			label:i18n("Advanced statistics"),
			tooltip:"Analyze the success of your giveaway to the last detail so that the next one you organize is even better.",
		}, {
			label:i18n("Show Mode"),
			tooltip:"Download and share a video of the moment that winners are selected.",
		}],
	}, {
		label:i18n("Instagram Giveaway"),
		icon:'ep-instagram',
		color:'var(--instagram-color)',
		versions:min_basic,
		data:[{
			label:i18n("Support for sponsored posts and dark posts"),
			tooltip:"Include comments on sponsored posts and posts created with Facebook Ads.",
			versions:min_basic_pro,
		}, {
			label:i18n("Giveaways with collaborators"),
			tooltip:"Include comments from several posts, including posts from collaborating brands, sponsors, or influencer accounts.",
			versions:min_basic_pro,
		}],
	}, {
		label:i18n("Instagram Stories Giveaway"),
		icon:'knk-story',
		color:'var(--instagram-color)',
		versions:min_basic_pro,
		data:[{
			label:i18n("Users reply to the brand's stories"),
			tooltip:"Participation is open to users who reply with a text message to the Brand's Story within the dates set by the organizer.",
		}, {
			label:i18n("Users mention the brand in their stories"),
			tooltip:"Includes users with public profiles that have mentioned the organizing brand in one of their stories.",
		}],
	}, {
		label:i18n("Facebook Giveaway"),
		icon:'ep-facebook',
		color:'var(--facebook-color)',
		versions:min_basic,
		data:[{
			label:i18n("Likes/Reactions to one or more posts"),
			tooltip:"Giveaway for users who react to one or more posts (maximum 10) on a Facebook Page.",
		}, {
			label:i18n("Comments on one or more posts"),
			tooltip:"Giveaway for users who comment on one or more posts (maximum 10) on a Facebook Page.",
		}, {
			label:i18n("Giveaways with collaborators"),
			tooltip:"Include comments from several posts, including posts from collaborators' accounts.",
			versions:min_basic_pro,
		}, {
			label:i18n("Support for dark posts"),
			tooltip:"Include reactions or comments on posts which are created with Facebook Ads and don't appear on your Page.",
			versions:min_basic_pro,
		}],
	}, {
		label:i18n("Twitter Giveaway"),
		icon:'ep-twitter',
		color:'var(--twitter-color)',
		versions:min_basic_pro,
		data:[{
			label:i18n("RTs of one or more tweets"),
			tooltip:"Giveaway for users who retweet one tweet (quote retweets included).",
			versions:min_basic_pro,
		}, {
			label:i18n("RTs of several tweets with a specific hashtag"),
			tooltip:"Giveaway for users who retweet several tweets with the same hashtag.",
			versions:min_basic_pro,
		}, {
			label:i18n("Replies to one tweet"),
			tooltip:"Giveaway for users who reply to one tweet.",
			versions:min_basic_pro,
		}, {
			label:i18n("Mention + Hashtag"),
			tooltip:"Giveaway for users who mention a brand on Twitter and include the brand's chosen hashtag in their tweet.",
			versions:min_basic_pro,
		}, {
			label:i18n("Support for sponsored tweets"),
			tooltip:"Include tweets created in Twitter Ads.",
			versions:min_basic_pro,
		}, {
			label:i18n("Maximum age of tweets"),
			tooltip:"Twitter giveaways are based on user interactions with tweets. If a tweet was published more than 7 days ago, its interactions can not be recovered.",
			versions:{ basic:`-`, basicpro:`7 ${i18n("days")}` },
		}],
	}, {
		label:i18n("Multi-Network Giveaway"),
		icon:'knk-diagram',
		versions:min_basic,
		data:[{
			label:i18n("Combine comments on Facebook and Instagram posts in one giveaway"),
			tooltip:"Select multiple posts, from multiple profiles, on multiple social networks. Import all comments, and select the winners at random. You'll also receive a certificate with the Easypromos warranty. Ideal for giveaways which combine posts on Facebook and Instagram.",
			versions:min_basic_pro,
		}, {
			label:i18n("Giveaways with collaborators"),
			tooltip:"Include comments from various posts, including posts by collaborators. Import comments from posts by sponsors, other brands, and influencers with the secure Invitations tool. Simply send a link - no need for collaborators to create an account or share passwords.",
			versions:min_basic_pro,
		}],
	}, {
		label:i18n("List Giveaway"),
		icon:'knk-ball-drum',
		versions:min_basic,
		data:[{
			label:i18n("Maximum number of participants"),
			tooltip:"Random prize draw among all entries that administrator uploads from an Excel or CSV file.",
			versions:{ basic:formatBigNumber(5000), basicpro:formatBigNumber(50000) },
		}, {
			label:i18n("Broadcast mode winner selection video"),
			tooltip:"Animated video to show the winners and prizes. Configure texts and all video elements on BASIC PRO.",
			versions:min_basic_pro,
		}],
	}, {
		label:i18n("Entry Form Giveaway"),
		icon:'knk-present',
		versions:min_basic,
		data:[{
			label:i18n("Maximum number of participants"),
			tooltip:"Random prize draw among all users registered in the ''Entry Form Giveaway'' microsite.",
			versions:{ basic:formatBigNumber(5000), basicpro:formatBigNumber(50000) },
		}, {
			label:i18n("Show mode video for winners"),
			tooltip:"Customize and broadcast an animated video to announce the giveaway winners and their associated prizes. You will attract participants via a registration form hosted on Easypromos.",
		}],
	}, {
		label:i18n("Giveaway for events"),
		icon:'knk-live',
		versions:min_basic,
		data:[{
			label:i18n("Maximum number of participants"),
			tooltip:"Choose the plan accordingly to the number of participants that you’re expecting. You will need a Premium plan for more than 500 participants.",
			versions:{ basic:formatBigNumber(200), basicpro:formatBigNumber(500) },
		}],
	}];

	const close = () => {
		setDisplay(false);
		if(props.close != undefined) props.close();
	};

	const total_columns = Object.keys(min_basic).length + 1;

	const col_styles = { width:'175px' };
	const header_row_styles = { height:'60px' };

	return (
		<>
			<ActionLink
				_class={props._class}
				onClick={() => setDisplay(true)}
			>{props?.label ?? "View comparison table"}</ActionLink>

			{(display) && (
				<FloatingLayer _class={{size:'big'}} backButton={close}>
					<div class="without_actions">
						<SectionTitle>Comparison Table: BASIC vs BASIC PRO</SectionTitle>

						<table class="gui_props_table in_top">
							<thead>
								<tr>
									<th />

									<th style={col_styles}>
										<div class="gui_hidden_overflowed" style={header_row_styles}>
											<EPPlatformVersion version="basic" />
										</div>
									</th>

									<th style={col_styles}>
										<div class="gui_hidden_overflowed" style={header_row_styles}>
											<EPPlatformVersion version="basicpro" />
										</div>
									</th>
								</tr>
							</thead>

							{sections.map((section, s) => {
								return (
									<tbody key={s}>
										{(section.label != undefined && section.label !== false) && (
											<tr class="section_header">
												<td colSpan={total_columns}>
													<span
														class={(section.icon != undefined) ? `icon ${section.icon}` : null}
														style={(section.color != undefined) ? { '--main-text-color':section.color } : null}
													>
														{i18n(section.label)}
													</span>
												</td>
											</tr>
										)}

										{section.data.map((item, i) => {
											const has_basic = item?.versions?.basic ?? (section?.versions?.basic ?? false);
											const has_basicpro = item?.versions?.basicpro ?? (section?.versions?.basicpro ?? false);

											const is_last = (i == section.data.length - 1) ? true : false;

											return (
												<tr key={i} class={(is_last) ? 'last_row' : null}>
													<td>
														<strong>{item.label}</strong>

														{(item.tooltip != undefined) && (
															<Tooltip>{item.tooltip}</Tooltip>
														)}
													</td>

													<td>
														{(typeof has_basic == 'boolean') ? (
															<BooleanIcon value={has_basic} />
														) : (
															<span>{has_basic}</span>
														)}
													</td>

													<td>
														{(typeof has_basicpro == 'boolean') ? (
															<BooleanIcon value={has_basicpro} />
														) : (
															<span>{has_basicpro}</span>
														)}
													</td>
												</tr>
											)
										})}
									</tbody>
								)
							})}
						</table>
					</div>
				</FloatingLayer>
			)}
		</>
	)
}

export default injectIntl(BasicPlansComparisonTable);